// CAPABILITY: Account self-service edits (email + password) via Firebase Auth.
// Email/password changes are client-side Firebase Auth operations and require a
// recent login, so each operation re-authenticates with the current password
// first. Email changes use verifyBeforeUpdateEmail (the change applies only
// after the user clicks the verification link sent to the new address).

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";
import { auth, firestore } from "../../firebase";

async function reauthenticate(currentPassword) {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error("You must be signed in to change your account.");
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  return user;
}

export async function changePassword({ currentPassword, newPassword }) {
  const user = await reauthenticate(currentPassword);
  await updatePassword(user, newPassword);
  console.info(`ACCOUNT_PASSWORD_CHANGED uid=${user.uid}`);
  return { ok: true };
}

export async function changeEmail({ currentPassword, newEmail }) {
  const user = await reauthenticate(currentPassword);
  const nextEmail = String(newEmail).trim();

  // Sends a verification link to the NEW address; the auth email switches only
  // after the user confirms it.
  await verifyBeforeUpdateEmail(user, nextEmail);

  // Analytics admin gating keys off accounts/{uid}.admins containing the token
  // email. Add the new email now (keep the old) so access keeps working both
  // before and after the user completes verification.
  try {
    await updateDoc(doc(firestore, "accounts", user.uid), { admins: arrayUnion(nextEmail) });
  } catch (error) {
    console.warn(`ACCOUNT_ADMINS_UPDATE_FAILED uid=${user.uid} reason=${error.message}`);
  }

  console.info(`ACCOUNT_EMAIL_CHANGE_REQUESTED uid=${user.uid}`);
  return { ok: true, pendingVerification: true };
}

// Map Firebase Auth error codes to friendly, user-facing messages.
export function mapAuthError(error) {
  const code = error?.code || "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Your current password is incorrect.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/weak-password":
      return "That password is too weak. Use at least 6 characters.";
    case "auth/email-already-in-use":
      return "That email is already in use by another account.";
    case "auth/invalid-email":
      return "That email address is not valid.";
    case "auth/requires-recent-login":
      return "Please sign out and back in, then try again.";
    case "auth/operation-not-allowed":
      return "This change isn't allowed for your account. Contact support.";
    default:
      return error?.message || "Something went wrong. Please try again.";
  }
}
